import { useEffect } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import VideaBrandSm from '../../common-components/elements/icon/svgs/VideaBrandSm';
import { usePreferences } from '../../react-query/usePreferences';
import { blueBlack400 } from '../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../../utils/user';
import {
  hasHuddleDataAtom,
  selectOnboardingCompleteAtom,
  selectOnboardingStepAtom,
  showOnboardingAtom,
} from '../patientList/atoms';

import * as Styled from './styles';
import {
  OnboardingContent,
  OnboardingEnd,
  OnboardingFooter,
  OnboardingHeader,
  OnboardingStart,
} from './subcomponents';
import { VCA_ONBOARDING_CONTENT } from './vca_content';
import { VCA_NO_HUDDLE_ONBOARDING_CONTENT } from './vca_no_huddle_content';
import { VDA_ONBOARDING_CONTENT } from './vda_content';
import { VDA_NO_HUDDLE_ONBOARDING_CONTENT } from './vda_no_huddle_content';

export const Onboarding = () => {
  const isVdaEnabled = useFeatureFlag(
    PracticeFeatureFlagKey.VIDEA_DENTAL_ASSIST_ENABLED,
  )?.value;
  const preferences = usePreferences();
  const isPlanningFlagDisabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.PLANNING_MODE_DISABLED,
  )?.value;
  const isPlanningModeDisabled =
    preferences.data?.isPlanningModeDisabled || isPlanningFlagDisabled;
  const hasHuddleData = useAtomValue(hasHuddleDataAtom);

  let slides:
    | (typeof VDA_ONBOARDING_CONTENT)['slides']
    | (typeof VDA_NO_HUDDLE_ONBOARDING_CONTENT)['slides']
    | (typeof VCA_ONBOARDING_CONTENT)['slides']
    | (typeof VCA_NO_HUDDLE_ONBOARDING_CONTENT)['slides'];
  if (isVdaEnabled) {
    slides =
      hasHuddleData && !isPlanningModeDisabled
        ? VDA_ONBOARDING_CONTENT.slides
        : VDA_NO_HUDDLE_ONBOARDING_CONTENT.slides;
  } else {
    slides =
      hasHuddleData && !isPlanningModeDisabled
        ? VCA_ONBOARDING_CONTENT.slides
        : VCA_NO_HUDDLE_ONBOARDING_CONTENT.slides;
  }

  const [currentSlide, setCurrentSlide] = useAtom(selectOnboardingStepAtom);
  const [isOnboardingComplete, setIsOnboardingComplete] = useAtom(
    selectOnboardingCompleteAtom,
  );

  const setShowOnboarding = useSetAtom(showOnboardingAtom);

  const { trackEvent } = useTrackEvent();

  const isStart = currentSlide === 0;

  useEffect(() => {
    if (isStart) {
      trackEvent(mixpanelEvents.onboardingStarted, {
        isVdaOnboarding: isVdaEnabled,
      });
    }
  }, [isStart, trackEvent, isVdaEnabled]);

  if (!slides[currentSlide] || isOnboardingComplete) return null;

  const isEnd = currentSlide === slides.length - 1;

  const handleBack = () => {
    const slideTemplate = document.getElementsByClassName('template');
    !!slideTemplate[0]?.scrollTo && slideTemplate[0].scrollTo(0, 0);
    if (isStart) return;
    if (isEnd) {
      setCurrentSlide(currentSlide - 1);
      return;
    }
    setCurrentSlide(currentSlide - 1);
  };

  const handleNext = () => {
    if (isEnd) return;
    setCurrentSlide(currentSlide + 1);
    const slideTemplate = document.getElementsByClassName('template');
    slideTemplate[0]?.scrollTo(0, 0);
    if (currentSlide + 1 === slides.length - 1) {
      trackEvent(mixpanelEvents.onboardingCompleted, {
        isVdaOnboarding: isVdaEnabled,
      });
    }
  };

  const navigateToApp = () => {
    const lastSlide = slides.length;
    setCurrentSlide(lastSlide);
    setIsOnboardingComplete(true);
    setShowOnboarding(false);
  };

  return (
    <Styled.OnboardingContainer>
      <div className="inner-onboard-container">
        <OnboardingHeader
          navigateToApp={navigateToApp}
          logo={<VideaBrandSm color={blueBlack400} />}
        />
        {isStart && <OnboardingStart />}
        {isEnd && <OnboardingEnd />}
        {!isStart && !isEnd && (
          <OnboardingContent
            template={slides[currentSlide].template}
            content={slides[currentSlide]}
          />
        )}
        <OnboardingFooter
          handleBack={handleBack}
          handleNext={handleNext}
          isStart={isStart}
          isEnd={isEnd}
          navigateToApp={navigateToApp}
        />
      </div>
    </Styled.OnboardingContainer>
  );
};
